/**
 * In-page fan-out of the backend's `activityContentChanged` push, plus a record of which Notes and
 * Documents tabs are on screen right now.
 *
 * One listener owns the WebSocket (useActivityContentUpdates) and re-emits here. An open tab
 * (useMarkContentAsRead) listens for its own activity: it reloads its list and marks it read again,
 * so new content that arrives while the tab is open never lights a dot. The WebSocket listener asks
 * `isContentOnScreen` first and leaves those activities to the tab, otherwise the two requests could
 * finish in the wrong order and bring the dot back.
 */

const listeners = new Set();
const onScreen = new Map();

const key = (activityId, contentType) => `${activityId}:${contentType}`;

export const subscribeActivityContentChanged = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const emitActivityContentChanged = (event) => {
    listeners.forEach((listener) => {
        try {
            listener(event);
        } catch (err) {
            // one broken listener must not keep the others from updating
        }
    });
};

// Counted rather than a flag: the drawer and the detail modal can both show the Notes of one activity.
export const registerContentOnScreen = (activityId, contentType) => {
    const k = key(activityId, contentType);
    onScreen.set(k, (onScreen.get(k) ?? 0) + 1);
    return () => {
        const count = (onScreen.get(k) ?? 1) - 1;
        if (count > 0) onScreen.set(k, count);
        else onScreen.delete(k);
    };
};

export const isContentOnScreen = (activityId, contentType) => onScreen.has(key(activityId, contentType));

/**
 * "Mark as unread" keeps the drawer or modal open, so a tab showing the activity can come back on screen
 * without the user choosing to read it (closing the modal reveals the drawer underneath). Until the user
 * opens a tab themselves, opens another activity or closes the drawer, such a tab must not mark the
 * content read again. See useMarkContentAsRead.
 */
const autoReadSuppressed = new Set();

export const suppressAutoRead = (activityId) => {
    autoReadSuppressed.add(key(activityId, "notes"));
    autoReadSuppressed.add(key(activityId, "documents"));
};

export const isAutoReadSuppressed = (activityId, contentType) => autoReadSuppressed.has(key(activityId, contentType));

export const releaseAutoRead = (activityId, contentType) => autoReadSuppressed.delete(key(activityId, contentType));

export const clearAutoReadSuppression = () => autoReadSuppressed.clear();
